import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  downloadCsv,
  getAccountInstructors,
} from '../../../requests/opsReports';
import {
  btnGhost,
  btnSecondary,
  Card,
  EmptyState,
  tableClass,
  tdClass,
  thClass,
  theadClass,
} from '../ui';
import AccountPicker, { TPickedAccount } from './AccountPicker';

/**
 * Who has taught an account — the "trainer history" a scheduler checks
 * before assigning a private class. Name order; no scores.
 */
const AccountInstructors: React.FC = () => {
  const [account, setAccount] = useState<TPickedAccount | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['opsAccountInstructors', account?.id],
    queryFn: () => getAccountInstructors(account!.id),
    enabled: !!account,
  });

  const csv = async () => {
    if (!account) return;
    setBusy(true);
    try {
      await downloadCsv(
        'ops/reports/account-instructors',
        { accountId: account.id },
        `instructors-${account.name.replace(/[^\w-]+/g, '_')}.csv`,
      );
    } catch (e) {
      showToast(resolveApiMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <AccountPicker
          value={account}
          onChange={setAccount}
          className="w-96"
          placeholder="Find an account to see who has taught them…"
        />
        {account && (
          <>
            <Link
              to={PATH.opsAccountView.replace(':id', account.id)}
              className={btnGhost}
            >
              Open account
            </Link>
            <span className="flex-1" />
            <button
              className={btnSecondary}
              onClick={csv}
              disabled={busy || !data}
            >
              {busy ? 'Preparing…' : 'Download CSV'}
            </button>
          </>
        )}
      </div>

      {!account ? (
        <Card>
          <EmptyState
            title="Pick an account"
            hint="The table lists every instructor who has taught them, how many classes, and the most recent date."
          />
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : data && data.items.length === 0 ? (
        <Card>
          <EmptyState
            title="No classes with a named instructor yet"
            hint={
              data.totalClasses > 0
                ? `${data.totalClasses} classes on record, none with an instructor name.`
                : 'This account has no classes on record.'
            }
          />
        </Card>
      ) : (
        data && (
          <Card
            title={`${data.accountName ?? 'Account'} · ${
              data.items.length
            } instructors`}
            right={
              <span className="text-xs text-gray-three">
                {data.totalClasses.toLocaleString()} classes
                {data.unassignedClasses > 0 &&
                  ` · ${data.unassignedClasses} without an instructor name`}
              </span>
            }
          >
            <div className="overflow-x-auto">
              <table className={`${tableClass} min-w-[900px]`}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Instructor</th>
                    <th className={`${thClass} text-right`}>Classes</th>
                    <th className={`${thClass} text-right`}>Upcoming</th>
                    <th className={`${thClass} text-right`}>Hours</th>
                    <th className={thClass}>First</th>
                    <th className={thClass}>Most recent</th>
                    <th className={thClass}>Most recent class</th>
                    <th className={`${thClass} text-right`}>Assignment rows</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.instructorName}>
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {r.instructorName}
                      </td>
                      <td className={`${tdClass} text-right`}>{r.classes}</td>
                      <td className={`${tdClass} text-right`}>
                        {r.upcoming > 0 ? (
                          r.upcoming
                        ) : (
                          <span className="text-gray-two">—</span>
                        )}
                      </td>
                      <td className={`${tdClass} text-right`}>{r.hours}</td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {r.firstDate?.slice(0, 10) ?? '—'}
                      </td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {r.lastDate?.slice(0, 10) ?? '—'}
                      </td>
                      <td
                        className={`${tdClass} max-w-xs truncate text-gray-three`}
                      >
                        {r.lastClassName ?? '—'}
                      </td>
                      <td className={`${tdClass} text-right text-gray-three`}>
                        {r.assignments}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-white-three px-5 py-3 text-xs text-gray-three">
              Counts come from the class record&apos;s instructor name;
              cancelled classes are left out. Also available filtered on the{' '}
              <Link
                to={`${PATH.opsAssignments}?accountId=${account.id}`}
                className="font-medium text-blue-primary hover:underline"
              >
                assignments screen
              </Link>
              .
            </div>
          </Card>
        )
      )}
    </>
  );
};

export default AccountInstructors;
